import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { fetchPatientPortalSession, type PatientSession } from '../../api/patientPortal/auth.ts';
import { isUnauthenticated } from '../../api/client.ts';
import { FailureNotice, LoadingNotice } from '../../components/AsyncState.tsx';
import { PATIENT_SESSION_QUERY_KEY, PatientSessionContext } from './PatientSessionContext.ts';

/**
 * The patient-portal route guard — the patient-facing mirror of `web/src/auth/RequireAuth.tsx`,
 * deliberately its own component rather than a parameterized version of that one (same "own
 * module, own cookie" reasoning as everything else under `api/patientPortal/`). Reads `:orgId` from
 * the URL rather than from any session, because the whole point of this guard is to run *before* a
 * session is known to exist — every patient-portal route lives under `/portal/:orgId/*`
 * (`docs/decisions/patient-portal-frontend-scope.md`'s routing design call), so the org that owns
 * this portal is always in the URL, never guessed.
 *
 * Same three outcomes as `RequireAuth`, same reasoning for keeping them apart: still checking
 * (render a status, redirect nothing), 401 (bounce to this org's own login, carrying where the
 * patient was trying to go), or any other failure (show it — a missing endpoint or a shape mismatch
 * is not "not signed in").
 */
export function RequirePatientSession({ children }: { readonly children: ReactNode }) {
  const { orgId } = useParams<{ orgId: string }>();
  const location = useLocation();

  const session = useQuery<PatientSession>({
    queryKey: PATIENT_SESSION_QUERY_KEY,
    queryFn: ({ signal }) => fetchPatientPortalSession(signal),
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });

  // `:orgId` is guaranteed present by the route this component is mounted under
  // (`/portal/:orgId/*`) — a missing value here means the router itself is misconfigured, not a
  // real runtime state to render a UI for.
  if (orgId === undefined) {
    throw new Error('RequirePatientSession rendered outside a /portal/:orgId route');
  }

  if (session.isPending) {
    return <LoadingNotice label="Checking your session…" />;
  }

  if (session.isError) {
    if (isUnauthenticated(session.error)) {
      return (
        <Navigate
          to={`/portal/${orgId}/login`}
          replace
          state={{ from: location.pathname + location.search }}
        />
      );
    }
    return (
      <main id="main" className="mx-auto w-full max-w-2xl p-4">
        <FailureNotice
          error={session.error}
          onRetry={() => {
            void session.refetch();
          }}
        />
      </main>
    );
  }

  return (
    <PatientSessionContext.Provider value={session.data}>{children}</PatientSessionContext.Provider>
  );
}
